import React, { useState, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { Listbox } from '@headlessui/react';
import IconClose from '$partials/icons/icon-close';
import { IconChevronUp, IconChevronDown } from '$partials/icons';

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 * @typedef {Object} ExtendedProps
 * @prop {('concrete'|'outline'|'underline')} [look=concrete] Style variant. Default: 'concrete'
 */
const Select = forwardRef((
    /**@type {Props} */
    {
        label,
        onChange = () => { },
        options,
        value = '',
        className,
        ...remainingProps
    },
    /**@type {React.ForwardedRef<HTMLInputElement>} */
    ref
) => {
    const [selectedOption, setSelectedOption] = useState(value);

    const handleChange = (value) => {
        onChange(value);
        setSelectedOption(value);
    };

    return (
        <div
            className={`relative ${className ? className : ''}`}
            tabIndex={1}>

            {/* Hidden input */}
            <input
                className='sr-only'
                ref={ref}
                type='hidden'
                {...remainingProps}
                value={selectedOption} />

            <Listbox
                onChange={handleChange}
                value={selectedOption}>
                {({ open }) => {
                    return (
                        <div className=''>
                            <Listbox.Button className='type-style-7 bg-white h-full py-10 px-20 rounded-full flex items-center'>
                                {label}
                                <div
                                    aria-hidden='true'
                                    className='mt-1 pt-1 pl-5'>
                                    {open && (
                                        <IconChevronUp/>
                                    )}
                                    {!open && (
                                        <IconChevronDown/>
                                    )}
                                </div>
                            </Listbox.Button>
                            {open && (
                                <div
                                    className={'absolute w-full md:w-300 md:max-h-200 overflow-y-auto bg-grey rounded-md z-20 top-50 p-25'}>
                                    <Listbox.Options className='flex flex-col gap-y-10'>
                                        {options.map((option, idx) => {
                                            return (
                                                <Listbox.Option
                                                    as={React.Fragment}
                                                    key={idx}
                                                    value={option.value}>
                                                    {({ selected }) => (
                                                        <li className={`type-style-7 self-start cursor-pointer select-none inline-flex items-center py-8 px-12 rounded-full border ${selected ? 'bg-black text-white border-black' : 'border-black/25'}`}>
                                                            {option.label}
                                                            <div
                                                                aria-hidden={true}
                                                                className='-mb-1 pl-8'>
                                                                {selected && (
                                                                    <IconClose fill='#ffffff'/>
                                                                )}
                                                            </div>
                                                        </li>
                                                    )}
                                                </Listbox.Option>
                                            );
                                        })}
                                    </Listbox.Options>
                                </div>
                            )}
                        </div>
                    );
                }}
            </Listbox>
        </div>
    );
});

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    onChange: PropTypes.func,
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    className: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })).isRequired
};

Select.propTypes = propTypes;
Select.displayName = 'Select';

export default Select;
